/**
 * VENNER — Regalo con compra
 *
 *  · <venner-gift>       vive en la página de producto. Al agregar el producto principal al carrito,
 *                        si el interruptor está encendido, mete el regalo en la misma operación.
 *  · <venner-gift-guard> vive en el carrito y en el cajón. Retira el regalo cuando ya no le corresponde;
 *                        quién sobra lo decide Liquid, aquí solo se ejecuta.
 *
 * El regalo nunca se agrega al encender el interruptor: si se hiciera, alguien podría quedarse con el
 * regalo suelto en el carrito y pagarlo completo. Se agrega solo después de que el servidor confirma
 * que el producto que lo justifica ya está dentro.
 */
if (!window.__vennerGiftReady) {
  window.__vennerGiftReady = true;

  const AVISO = 'venner:regalo-retirado';
  const raiz = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /** Las secciones que piden repintarse (carrito, cajón) para que el cambio se vea de inmediato. */
  function secciones() {
    const lista = [];

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: lista } })
    );

    return lista.join(',');
  }

  function anuncia(carrito) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        // `line-item:change` repinta el carrito sin volver a abrir el cajón: Prestige ya lo abrió.
        detail: { baseEvent: 'line-item:change', cart: carrito }
      })
    );
  }

  function guarda(clave, valor) {
    try {
      if (valor) sessionStorage.setItem(clave, valor);
      else sessionStorage.removeItem(clave);
    } catch (error) {
      // Navegación privada: el aviso se pierde, el carrito no.
    }
  }

  function recupera(clave) {
    try {
      const valor = sessionStorage.getItem(clave);
      sessionStorage.removeItem(clave);
      return valor;
    } catch (error) {
      return null;
    }
  }

  // ------------------------------------------------------------------ página de producto

  class VennerGift extends HTMLElement {
    connectedCallback() {
      this.interruptor = this.querySelector('.vgift__input');

      if (!this.interruptor || !this.formulario()) return;

      if (this.dataset.hideDynamicCheckout) {
        document.documentElement.classList.add('venner-gift-hides-dynamic');
      }

      this.alCambiar = () => this.sincroniza();
      this.alAgregar = (evento) => this.cuandoSeAgrega(evento);

      this.interruptor.addEventListener('change', this.alCambiar);
      document.addEventListener('variant:add', this.alAgregar);

      this.sincroniza();
    }

    disconnectedCallback() {
      this.interruptor?.removeEventListener('change', this.alCambiar);
      document.removeEventListener('variant:add', this.alAgregar);
      document.documentElement.classList.remove('venner-gift-on');
    }

    /**
     * El formulario se busca cada vez, nunca se guarda: al cambiar de variante Prestige repinta esa
     * parte de la página con HTML del servidor y una referencia guardada apuntaría a un elemento
     * que ya no está en la página.
     */
    formulario() {
      return document.forms[this.dataset.form];
    }

    /**
     * El texto del botón de compra se completa desde CSS, no escribiendo en el botón: Prestige
     * reemplaza <buy-buttons> con HTML del servidor en cada cambio de variante y borraría cualquier
     * cosa que le hubiéramos puesto. Una clase en <html> sí sobrevive.
     */
    sincroniza() {
      document.documentElement.classList.toggle('venner-gift-on', this.interruptor.checked);
    }

    async cuandoSeAgrega(evento) {
      if (evento.target !== this.formulario() || !this.interruptor.checked) return;

      // Dos clics seguidos en «Agregar al carrito» traen dos avisos, y el segundo llega con un carrito
      // que todavía no incluye el regalo del primero: sin esta bandera se agregarían dos.
      if (this.agregando) return;

      const carrito = evento.detail && evento.detail.cart;
      const lineas = (carrito && carrito.items) || [];
      const califica = lineas.some((linea) => linea.handle === this.dataset.handle);
      const yaEsta = lineas.some((linea) => linea.properties && linea.properties._regalo_de);

      // El servidor manda: solo si confirma que el producto que lo justifica entró, entra el regalo.
      if (!califica || yaEsta) return;

      this.agregando = true;

      try {
        await this.agregaRegalo();
      } finally {
        this.agregando = false;
      }
    }

    async agregaRegalo() {
      const campo = this.querySelector('[data-vgift-variant]');

      if (!campo || !campo.value) return;

      const datos = new FormData();

      datos.set('id', campo.value);
      datos.set('quantity', '1');
      datos.set('properties[_regalo_de]', this.dataset.handle || '');

      if (this.dataset.until) datos.set('properties[_regalo_hasta]', this.dataset.until);
      if (this.dataset.note) datos.set('properties[_regalo_nota]', this.dataset.note);
      if (this.dataset.removedNotice) datos.set('properties[_regalo_aviso]', this.dataset.removedNotice);

      datos.set('sections', secciones());

      try {
        const respuesta = await fetch(`${raiz()}cart/add.js`, {
          method: 'POST',
          body: datos,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!respuesta.ok) {
          this.avisaDelFallo();
          return;
        }

        const agregado = await respuesta.json();
        const carrito = await (await fetch(`${raiz()}cart.js`)).json();

        carrito.sections = agregado.sections;
        anuncia(carrito);
      } catch (error) {
        this.avisaDelFallo();
      }
    }

    /**
     * Si el regalo no entró, el pedido sigue siendo válido: lo único que falta es el regalo. Se avisa
     * en la tarjeta en vez de bloquear nada.
     */
    avisaDelFallo() {
      if (!this.dataset.error) return;

      let aviso = this.querySelector('.vgift__error');

      if (!aviso) {
        aviso = document.createElement('p');
        aviso.className = 'vgift__error';
        aviso.setAttribute('role', 'alert');
        this.querySelector('.vgift__card')?.appendChild(aviso);
      }

      aviso.textContent = this.dataset.error;
    }
  }

  // ------------------------------------------------------------------ carrito y cajón

  /*
    Si el carrito rechaza el cambio, la sección se vuelve a dibujar con la misma línea y el guardián
    volvería a intentarlo sin fin. Se recuerda lo ya intentado para no entrar en ese bucle.
  */
  const intentadas = new Set();

  class VennerGiftGuard extends HTMLElement {
    async connectedCallback() {
      const clave = this.dataset.lineKey;

      if (!clave) {
        this.muestraAviso();
        return;
      }

      if (intentadas.has(clave)) return;

      intentadas.add(clave);

      const cantidad = parseInt(this.dataset.quantity, 10) || 0;

      guarda(AVISO, cantidad === 0 ? this.dataset.notice : '');

      try {
        const respuesta = await fetch(`${raiz()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: clave, quantity: cantidad, sections: secciones() })
        });

        if (!respuesta.ok) return;

        if (window.themeVariables?.settings?.pageType === 'cart') {
          window.location.reload();
          return;
        }

        anuncia(await respuesta.json());
      } catch (error) {
        // El carrito no cambió: en el peor caso el regalo sigue ahí y el siguiente repintado reintenta.
        intentadas.delete(clave);
      }
    }

    /** El aviso se muestra ya sin la línea, cuando el carrito se volvió a dibujar. */
    muestraAviso() {
      const texto = recupera(AVISO);

      if (!texto) return;

      const parrafo = document.createElement('p');

      parrafo.className = 'vgift-guard__notice';
      parrafo.setAttribute('role', 'status');
      parrafo.textContent = texto;
      this.appendChild(parrafo);
    }
  }

  if (!window.customElements.get('venner-gift')) {
    window.customElements.define('venner-gift', VennerGift);
  }

  if (!window.customElements.get('venner-gift-guard')) {
    window.customElements.define('venner-gift-guard', VennerGiftGuard);
  }
}
